import { LitElement, html, unsafeCSS, type PropertyValues } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import styles from './csv-import-modal-shell.css?inline';
import { STEPS, stepIndex, type StepId } from '../../constants/steps';
import { deepActiveElement } from '../../utils/deep-active-element';

import '@inmar/component-lib';

// ============================================================================
// Module-level constants
// ============================================================================

/** Pre-computed stepper step labels — stable reference, never re-allocated. */
const STEPPER_STEPS = STEPS.map((s) => ({ label: s.label }));

// ============================================================================
// Types
// ============================================================================

// (No exported event types — intent events carry empty detail per architecture doc)

// ============================================================================
// Global augmentations
// ============================================================================

declare global {
  interface HTMLElementTagNameMap {
    'csv-import-modal-shell': CsvImportModalShell;
  }
}

// ============================================================================
// Component
// ============================================================================

/**
 * Shared modal chrome for the CSV Import multi-step flow.
 *
 * Renders `<inmar-hc-modal>` (always open — the orchestrator controls
 * visibility by conditionally rendering this element). Once mounted, the
 * shell stays in the DOM across step transitions to avoid dialog remount
 * flashes. It is unmounted when the workflow closes.
 *
 * @fires csv-import-close - Fired when the user clicks the X button.
 *
 * @slot step-body - Step body content (setup-body, review-body, confirm-body).
 * @slot footer    - Footer buttons (Next/Back/Cancel/Submit per step).
 */
@customElement('csv-import-modal-shell')
export class CsvImportModalShell extends LitElement {
  static readonly styles = [unsafeCSS(styles)];

  // ============================================================================
  // Properties
  // ============================================================================

  /** Title text displayed in the modal header. */
  @property({ type: String, attribute: 'modal-title' })
  modalTitle = '';

  /** The current step identifier. Converted to 1-based index for the stepper. */
  @property({ type: String, attribute: 'current-step' })
  currentStep: StepId = 'select-file';

  /**
   * True while the orchestrator renders the cancel-confirmation panel into
   * the slots. Drives programmatic focus only — the shell renders slot content
   * unconditionally. Reflected so the attribute is available for styling/tests.
   */
  @property({ type: Boolean, reflect: true, attribute: 'cancel-confirm' })
  cancelConfirm = false;

  // ============================================================================
  // Private state
  // ============================================================================

  /** Element that held focus when the panel was shown; focus returns here on dismiss. */
  private _triggerEl: HTMLElement | null = null;

  // ============================================================================
  // Lifecycle
  // ============================================================================

  protected override updated(changed: PropertyValues): void {
    if (changed.has('cancelConfirm')) {
      if (this.cancelConfirm) {
        this._triggerEl = deepActiveElement();
        void this.updateComplete.then(() => this._focusInitialInPanel());
      } else if (this._triggerEl) {
        const el = this._triggerEl;
        this._triggerEl = null;
        void this.updateComplete.then(() => el.focus());
      }
    } else if (this.cancelConfirm) {
      // Panel stayed open but view changed (e.g. download-report → download-report-success).
      // The previously focused button may have been removed — re-seed focus.
      void this.updateComplete.then(() => {
        const active = deepActiveElement();
        const isInsidePanel =
          active && this.contains(active) && active !== document.body;
        if (!isInsidePanel) {
          this._focusInitialInPanel();
        }
      });
    }
  }

  /**
   * Focuses the panel's designated non-destructive action button.
   * The template marks the target with `data-initial-focus` (per PO decision:
   * seed focus on the option that does NOT cancel/leave, so discarding work
   * requires an explicit choice). Falls back to the first footer button if no
   * marker is present.
   */
  private _focusInitialInPanel(): void {
    const target =
      this.querySelector<HTMLElement>(
        'inmar-hc-button[slot="footer"][data-initial-focus]',
      ) ?? this.querySelector<HTMLElement>('inmar-hc-button[slot="footer"]');
    target?.focus();
  }

  // ============================================================================
  // Event handlers
  // ============================================================================

  private _onInnerModalClose(e: Event) {
    e.preventDefault();
    this.dispatchEvent(
      new CustomEvent('csv-import-close', {
        bubbles: true,
        composed: true,
      }),
    );
  }

  // ============================================================================
  // Render
  // ============================================================================

  override render() {
    return html`
      <inmar-hc-modal
        .open=${true}
        modal-title=${this.modalTitle}
        size="lg"
        .backdropDismiss=${false}
        @close=${this._onInnerModalClose}
      >
        <div slot="body">
          <div class="stepper-container">
            <inmar-hc-stepper
              .steps=${STEPPER_STEPS}
              current-step=${stepIndex(this.currentStep)}
            ></inmar-hc-stepper>
          </div>
          <slot name="step-body"></slot>
        </div>

        <slot name="footer" slot="footer"></slot>
      </inmar-hc-modal>
    `;
  }
}
